import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import {
  SearchFormComponent,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import { generateCode128Svg } from '@shared/utils/code128.util';

import { SkuBarcodeControllerService } from '@app/api/goods-service/api/skuBarcodeController.service';
import { SkuBarcodeVO } from '@app/api/goods-service/model/skuBarcodeVO';
import { ThemeService } from '@app/core/core';
import { PermissionService } from '@app/core/services/permission.service';
import {
  SKU_BARCODE_COLUMNS,
  SKU_BARCODE_DEFAULT_COLUMNS_ORDER,
} from './sku-barcode.config';

const LAZY_BATCH_SIZE = 20;

@Component({
  selector: 'app-sku-barcode',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    AppButtonComponent,
    AdvancedDataTableComponent,
    SearchFormComponent,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './sku-barcode.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkuBarcodeComponent implements OnInit, OnDestroy {
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  private readonly destroy$ = new Subject<void>();
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly skuBarcodeApi = inject(SkuBarcodeControllerService);
  private readonly sanitizer = inject(DomSanitizer);
  protected readonly themeService = inject(ThemeService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    { initialValue: this.permissionService.getCurrentPermissions() },
  );

  rows = signal<SkuBarcodeVO[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  pageSize = 20;
  page = signal(0);

  private renderedBarcodeCount = signal(LAZY_BATCH_SIZE);
  private barcodeCache = new Map<string, SafeHtml>();

  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.product.sku_barcode.view');
  });

  canPrint = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.product.sku_barcode.print');
  });

  searchFormConfig: FormFieldConfig[] = [
    {
      key: 'skuCode',
      label: 'sku_barcode.search.sku_code',
      type: 'text',
      placeholder: 'sku_barcode.search.enter_sku_code',
    },
    {
      key: 'skuName',
      label: 'sku_barcode.search.sku_name',
      type: 'text',
      placeholder: 'sku_barcode.search.enter_sku_name',
    },
    {
      key: 'barcodeType',
      label: 'sku_barcode.search.barcode_type',
      type: 'text',
      placeholder: 'sku_barcode.search.enter_barcode_type',
    },
    {
      key: 'barcodeValue',
      label: 'sku_barcode.search.barcode_value',
      type: 'text',
      placeholder: 'sku_barcode.search.enter_barcode_value',
    },
  ];

  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      SKU_BARCODE_COLUMNS.find((c) => c.prop === prop);
    const cols: TableColumn[] = [];
    for (const key of SKU_BARCODE_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }
    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => []);

  private searchParams: Record<string, any> = {};

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(): void {
    this.loading.set(true);
    this.barcodeCache.clear();
    this.renderedBarcodeCount.set(LAZY_BATCH_SIZE);

    this.skuBarcodeApi
      .page({
        page: this.page(),
        size: this.pageSize,
        ...this.searchParams,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.rows.set(res.pageContent ?? []);
          this.totalRecords.set(res.totalElements ?? 0);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant('sku_barcode.messages.load_error'),
          });
        },
      });
  }

  onSearch(searchData: any): void {
    this.searchParams = { ...searchData };
    this.page.set(0);
    this.loadData();
  }

  onReset(): void {
    this.searchParams = {};
    this.page.set(0);
    this.loadData();
  }

  onPageChange(event: any): void {
    this.page.set(event.page);
    this.pageSize = event.rows;
    this.loadData();
  }

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;
    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }

  getBarcodeHtml(value: string | undefined, index: number): SafeHtml | null {
    if (!value || index >= this.renderedBarcodeCount()) {
      return null;
    }
    let cached = this.barcodeCache.get(value);
    if (!cached) {
      const svg = generateCode128Svg(value, 40);
      cached = this.sanitizer.bypassSecurityTrustHtml(svg);
      this.barcodeCache.set(value, cached);
    }
    return cached;
  }

  onTableScroll(): void {
    const current = this.renderedBarcodeCount();
    if (current < this.rows().length) {
      this.renderedBarcodeCount.set(
        Math.min(current + LAZY_BATCH_SIZE, this.rows().length),
      );
    }
  }

  onPrint(): void {
    const currentRows = this.rows();
    if (!currentRows.length) {
      return;
    }

    const cards = currentRows
      .map((row) => {
        const svg = generateCode128Svg(row.barcodeValue ?? '', 50);
        return `
          <div class="barcode-card">
            <div class="info"><strong>${row.skuCode ?? ''}</strong> — ${row.skuName ?? ''}</div>
            <div class="barcode">${svg}</div>
            <div class="value">${row.barcodeValue ?? ''}</div>
          </div>`;
      })
      .join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>SKU Barcodes</title>
      <style>
        body{font-family:Arial,sans-serif;margin:20px}
        .barcode-card{border:1px solid #ccc;padding:12px;margin:8px 0;page-break-inside:avoid;text-align:center}
        .info{margin-bottom:6px;font-size:13px}
        .barcode svg{max-width:260px;height:50px}
        .value{font-size:12px;margin-top:4px;letter-spacing:1px}
        @media print{body{margin:0}.barcode-card{border:none;border-bottom:1px solid #eee}}
      </style></head><body>${cards}</body></html>`;

    const w = window.open('', '_blank');
    if (w) {
      w.document.write(html);
      w.document.close();
      w.focus();
      w.print();
    }
  }
}
